/**
 * Admin Orca Project RPC definitions
 *
 * Endpoints for the admin orca project list + detail page. The list shape
 * carries enough to render a status pill, progress bar, and timestamps; the
 * detail shape includes the server-rendered Mermaid dependency graph string,
 * an issue-by-issue table joined to the latest orca_task phase, and a recent
 * events feed.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OrcaProjectStatus, OrcaVerifyStatus } from "@fm/db/models/OrcaProject";
import { OrcaRepoMode } from "@fm/db/models/OrcaRepo";
import { OrcaTaskPhase } from "@fm/db/models/OrcaTask";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Shared value schemas
// ============================================================================

/**
 * Roll-up PR state as surfaced on the projects list + detail. Only `"open"` is
 * produced today — the roll-up URL is stamped when the PR is opened and no
 * GitHub read reconciles the merged/closed states yet (deferred to a follow-up).
 * The wider vocabulary is declared so the frame can render a state pill without
 * a wire change once that read lands.
 *
 * @since 1.0.0
 */
export const OrcaRollupPrState = Schema.Literal("open", "merged", "closed");
export type OrcaRollupPrState = Schema.Schema.Type<typeof OrcaRollupPrState>;

/**
 * The integration roll-up PR pointer, or `null` when no roll-up has been opened
 * yet. `number` / `url` come straight off the `orca_projects` row; `state` is
 * `"open"` until a GitHub read is added.
 *
 * @since 1.0.0
 */
export const OrcaRollupPr = Schema.Struct({
  number: Schema.Number,
  url: Schema.String,
  state: OrcaRollupPrState,
});
export type OrcaRollupPr = Schema.Schema.Type<typeof OrcaRollupPr>;

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Wraps Linear-fetch failures so the transport layer carries a structured
 * tag instead of a raw error string.
 *
 * @since 1.0.0
 */
export class OrcaProjectLinearError extends Schema.TaggedError<OrcaProjectLinearError>()(
  "OrcaProjectLinearError",
  {
    message: Schema.String,
  },
) {}

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Per-row shape for the project list endpoint. `passedCount` / `totalCount`
 * power the inline progress bar; `linearProjectName` is fetched from Linear
 * server-side so the table can render a human label rather than a UUID.
 *
 * @since 1.0.0
 */
export const OrcaProjectSummary = Schema.Struct({
  id: Schema.String,
  linearProjectId: Schema.String,
  linearProjectName: Schema.String,
  status: OrcaProjectStatus,
  passedCount: Schema.Number,
  totalCount: Schema.Number,
  startedAt: Schema.NullOr(Schema.DateTimeUtc),
  lastSweptAt: Schema.NullOr(Schema.DateTimeUtc),
  lastSyncedAt: Schema.DateTimeUtc,
  notifyChannel: Schema.NullOr(Schema.String),
  // OL-13 board/projects wire seam. `repo` is the project's target GitHub repo
  // (`owner/name`) or null (falls back to the global default); `mode` is that
  // repo's build mode joined from `orca_repos`, null when the repo has no
  // registry row. `integrationBranch` / `maxParallel` / `verifyStatus` come off
  // the project row. The four counts summarise the walk: `runningCount` =
  // orca_tasks in a non-terminal phase, `failedCount` = FAILED tasks,
  // `readyCount` / `blockedCount` = the analyzer's ready/blocked buckets over
  // the project's Linear issues. `rollupPr` is the roll-up PR pointer or null.
  repo: Schema.NullOr(Schema.String),
  mode: Schema.NullOr(OrcaRepoMode),
  integrationBranch: Schema.NullOr(Schema.String),
  maxParallel: Schema.Number,
  verifyStatus: Schema.NullOr(OrcaVerifyStatus),
  runningCount: Schema.Number,
  readyCount: Schema.Number,
  blockedCount: Schema.Number,
  failedCount: Schema.Number,
  rollupPr: Schema.NullOr(OrcaRollupPr),
});
export type OrcaProjectSummary = Schema.Schema.Type<typeof OrcaProjectSummary>;

/**
 * Per-issue row for the detail page. `phase` carries either a real
 * `OrcaTaskPhase` literal when a task has been dispatched, or the sentinel
 * "PENDING" string when Linear lists the issue but no orca_task exists yet.
 * `blockedBy` is populated for Linear issues whose `blocks` predecessors
 * haven't completed — used to render "PENDING (blocked by X)".
 *
 * @since 1.0.0
 */
export const OrcaProjectIssueRow = Schema.Struct({
  identifier: Schema.String,
  title: Schema.String,
  url: Schema.String,
  phase: Schema.Union(OrcaTaskPhase, Schema.Literal("PENDING")),
  // OL-13: `blockedBy` is now populated for EVERY row — tasked or not — so the
  // detail view can show a tasked issue's still-open predecessors, not only
  // untasked ones. Carries the identifiers of the issue's in-project `blocks`
  // predecessors whose Linear state is not completed/canceled.
  blockedBy: Schema.Array(Schema.String),
  // OL-13: topological wave (1-based level) of the issue within the project's
  // in-project `blocks` graph — 1 = no unresolved blocker, n = 1 + max(level of
  // its in-project blockers). Longest path. Cycle-safe. Computed in this seam.
  wave: Schema.Number,
  // OL-13: `attempt`/`maxAttempts` of the issue's orca_task as a `"2/3"` label,
  // the runner it is placed on, and how long it has been in its current phase
  // (compact human duration). All null when the issue has no orca_task yet.
  attempt: Schema.NullOr(Schema.String),
  runnerName: Schema.NullOr(Schema.String),
  inPhaseAge: Schema.NullOr(Schema.String),
  prNumber: Schema.NullOr(Schema.Number),
  prUrl: Schema.NullOr(Schema.String),
  orcaTaskId: Schema.NullOr(Schema.String),
  updatedAt: Schema.NullOr(Schema.DateTimeUtc),
  // CLA-398: S3 console link to this issue's archived run artifacts/logs.
  // Always derivable from the issue identifier, so it is non-null whenever the
  // detail handler is wired with the archive config (null only in bare unit
  // call paths that omit it).
  archiveUrl: Schema.NullOr(Schema.String),
});
export type OrcaProjectIssueRow = Schema.Schema.Type<typeof OrcaProjectIssueRow>;

/**
 * Per-event entry for the recent-events feed. Driven by the most recent
 * orca_task `updated_at` values across the project, capped at 20 entries.
 *
 * @since 1.0.0
 */
export const OrcaProjectEventRow = Schema.Struct({
  orcaTaskId: Schema.String,
  identifier: Schema.String,
  phase: OrcaTaskPhase,
  updatedAt: Schema.DateTimeUtc,
});
export type OrcaProjectEventRow = Schema.Schema.Type<typeof OrcaProjectEventRow>;

/**
 * Detail-page payload. Includes the summary fields plus the Mermaid string,
 * the per-issue table rows, and the recent events list.
 *
 * @since 1.0.0
 */
export const OrcaProjectDetail = Schema.Struct({
  summary: OrcaProjectSummary,
  linearProjectUrl: Schema.String,
  completedAt: Schema.NullOr(Schema.DateTimeUtc),
  blockedAt: Schema.NullOr(Schema.DateTimeUtc),
  errorMessage: Schema.NullOr(Schema.String),
  // OL-42: when the project's cached `blocks` graph was last verified against
  // Linear. NULL means orca does not trust the graph and every sweep entry point
  // refuses to dispatch — the board renders "graph unverified — dispatch paused".
  // Recovery is automatic (the reconciler re-fetches and stamps), so this is a
  // status indicator, not an action item.
  graphVerifiedAt: Schema.NullOr(Schema.DateTimeUtc),
  mermaid: Schema.String,
  issues: Schema.Array(OrcaProjectIssueRow),
  recentEvents: Schema.Array(OrcaProjectEventRow),
});
export type OrcaProjectDetail = Schema.Schema.Type<typeof OrcaProjectDetail>;

// ============================================================================
// Request Schemas
// ============================================================================

export const OrcaProjectGetInput = Schema.Struct({
  id: Schema.String,
  refresh: Schema.optional(Schema.Boolean),
});

export const OrcaProjectStreamInput = Schema.Struct({
  id: Schema.String,
});

export const OrcaProjectRetryInput = Schema.Struct({
  orcaTaskId: Schema.String,
});

export const OrcaProjectCancelInput = Schema.Struct({
  id: Schema.String,
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Project RPC group.
 *
 * All endpoints require admin role via AdminMiddleware. Prefixed with
 * `admin.orcaProject.` per the existing admin-rpc naming convention.
 *
 * @since 1.0.0
 */
export class AdminOrcaProjectRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaProject.list", {
    success: Schema.Array(OrcaProjectSummary),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, OrcaProjectLinearError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaProject.get", {
    payload: OrcaProjectGetInput,
    success: OrcaProjectDetail,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError, OrcaProjectLinearError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaProject.stream", {
    payload: OrcaProjectStreamInput,
    success: OrcaProjectDetail,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError, OrcaProjectLinearError),
    stream: true,
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaProject.retry", {
    payload: OrcaProjectRetryInput,
    success: Schema.Struct({ orcaTaskId: Schema.String }),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaProject.cancel", {
    payload: OrcaProjectCancelInput,
    success: Schema.Struct({ canceled: Schema.Boolean }),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError),
  }).middleware(AdminMiddleware),
) {}
